import { Link } from "react-router-dom";
import { Rocket, MapPin, Calendar, ExternalLink } from "lucide-react";
import { CountdownTimer } from "./CountdownTimer";
import { ShareButton } from "./ShareButton";
import { CalendarButton } from "./CalendarButton";
import { FavoriteButton } from "./FavoriteButton";
import { format } from "date-fns";

const getStatusStyle = (status) => {
  const statusLower = status?.toLowerCase() || "";
  if (statusLower.includes("go") || statusLower.includes("confirmed")) return "status-go";
  if (statusLower.includes("tbd") || statusLower.includes("tbc")) return "status-tbd";
  if (statusLower.includes("hold") || statusLower.includes("scrub")) return "status-hold";
  return "status-tbd";
};

export const LaunchCard = ({ launch, featured = false }) => {
  const launchDate = launch.net ? new Date(launch.net) : null;
  const formattedDate = launchDate
    ? format(launchDate, "MMM d, yyyy 'at' h:mm a")
    : "Date TBD";

  return (
    <div
      className={`glass-card-hover card-shine overflow-hidden ${
        featured ? "p-6 md:p-8" : "p-4 md:p-6"
      }`}
      data-testid={`launch-card-${launch.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className={`status-badge ${getStatusStyle(launch.status)}`}>
              {launch.status_abbrev || launch.status}
            </span>
            <span className="text-xs text-slate-500 font-heading uppercase tracking-wider">
              {launch.provider}
            </span>
          </div>
          <Link
            to={`/launches/${launch.id}`}
            className="block group"
            data-testid={`launch-link-${launch.id}`}
          >
            <h3 className={`font-heading font-bold text-white group-hover:text-cosmic-blue-glow ${
              featured ? "text-xl md:text-2xl" : "text-lg"
            }`}
            style={{ transition: "color 0.2s ease" }}
            >
              {launch.name}
            </h3>
          </Link>
        </div>
        {launch.image && (
          <img
            src={launch.image}
            alt={launch.rocket}
            className={`rounded-lg object-cover ${
              featured ? "w-20 h-20 md:w-24 md:h-24" : "w-16 h-16"
            }`}
            loading="lazy"
          />
        )}
      </div>

      {/* Info */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Rocket className="w-4 h-4 text-cosmic-blue" strokeWidth={1.5} />
          <span>{launch.rocket}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <MapPin className="w-4 h-4 text-ignition-red" strokeWidth={1.5} />
          <span>{launch.pad_name}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Calendar className="w-4 h-4 text-space-gold" strokeWidth={1.5} />
          <span>{formattedDate}</span>
        </div>
      </div>

      {/* Mission Description */}
      {featured && launch.mission_description && (
        <p className="text-sm text-slate-400 mb-4 line-clamp-2">
          {launch.mission_description}
        </p>
      )}

      {/* Countdown */}
      {launchDate && (
        <div className={`mb-4 ${featured ? "py-4" : "py-2"}`}>
          <p className="text-xs text-slate-500 uppercase tracking-widest mb-2 text-center font-heading">
            T-Minus
          </p>
          <CountdownTimer targetDate={launch.net} compact={!featured} />
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 flex-wrap">
        <Link
          to={`/launches/${launch.id}`}
          className="btn-primary text-sm py-2 px-4"
          data-testid={`launch-details-btn-${launch.id}`}
        >
          Details
        </Link>
        
        {launch.webcast ? (
          <a
            href={launch.webcast}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline text-sm py-2 px-4 flex items-center gap-1"
            data-testid={`launch-webcast-${launch.id}`}
          >
            <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
            Watch
          </a>
        ) : (
          <span className="text-xs text-slate-500 px-2">Webcast pending</span>
        )}

        <div className="flex-1" />

        <FavoriteButton launch={launch} />
        <CalendarButton launch={launch} />
        <ShareButton launch={launch} />
      </div>
    </div>
  );
};

export const LaunchCardSkeleton = ({ featured = false }) => (
  <div className={`glass-card ${featured ? "p-6 md:p-8" : "p-4 md:p-6"}`}>
    <div className="flex items-start justify-between gap-4 mb-4">
      <div className="flex-1 space-y-2">
        <div className="skeleton h-5 w-20 rounded" />
        <div className="skeleton h-6 w-3/4 rounded" />
      </div>
      <div className={`skeleton rounded-lg ${featured ? "w-24 h-24" : "w-16 h-16"}`} />
    </div>
    <div className="space-y-2 mb-4">
      <div className="skeleton h-4 w-1/2 rounded" />
      <div className="skeleton h-4 w-2/3 rounded" />
      <div className="skeleton h-4 w-1/3 rounded" />
    </div>
    <div className="skeleton h-16 w-full rounded mb-4" />
    <div className="flex gap-2">
      <div className="skeleton h-10 w-24 rounded-xl" />
      <div className="skeleton h-10 w-20 rounded-xl" />
    </div>
  </div>
);
